/**
 * Schermata di panchina.
 *
 * Progettata per essere usata in piedi, con una mano, al sole, guardando lo
 * schermo per un secondo alla volta. Da qui discendono le scelte visive:
 * cronometro grande a cifre fisse, righe a tutta larghezza perche ogni pixel
 * di margine e un pulsante piu piccolo, bersagli alti almeno 52px, colore dei
 * pulsanti per esito e non per categoria. Componenti e colori sono quelli del
 * tema, con le misure da campo date sul posto: il file `timer.css` tiene solo
 * cio che Tailwind non esprime.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';
import { RUOLI_GIOCATORE, verificaIngresso, verificaRipartenza } from '@calcio/shared';
import type {
  EventoIn,
  Formato,
  StatoCampo,
  RuoloGiocatore,
  StatisticheGiocatore,
  StatoCronometro,
  TipoEvento,
} from '@calcio/shared';
import {
  ArrowRightLeft,
  ChevronLeft,
  Info,
  ListOrdered,
  LogIn,
  LogOut,
  OctagonX,
  Pause,
  Play,
  Trash2,
  TriangleAlert,
  Whistle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { chiama } from '../api/client.js';
import { ETICHETTA_EVENTO, nomeBreve, punteggio } from '../partite/partita.js';
import { IconaEvento, PulsanteEvento } from '../partite/eventi.js';
import { REPARTI, etichettaRuolo, repartoDi } from '../giocatori/elenchi.js';
import { avvisoPortieri } from './portieri.js';
import { useSchermoAcceso, useTimerPartita, type Momento } from './useTimerPartita.js';
import './timer.css';

interface Giocatore {
  id: string;
  numero: number | null;
  cognome: string;
  nome: string;
  ruolo: RuoloGiocatore;
  convocato: boolean;
  titolare: boolean;
}

/**
 * Eventi di gioco, sotto il nome di chi e in campo. A coppie di gemelli, il
 * buono accanto al cattivo: la griglia ha sempre un numero pari di colonne,
 * cosi ogni coppia resta sulla stessa riga. L'assist non sta qui: lo chiede
 * il gol, dopo il marcatore.
 * Etichette corte: il dirigente cerca colore e icona, non legge.
 */
const GEMELLI: { tipo: TipoEvento; etichetta: string }[][] = [
  [
    { tipo: 'passaggio_completato', etichetta: 'Passaggio' },
    { tipo: 'passaggio_sbagliato', etichetta: 'Sbagliato' },
  ],
  [
    { tipo: 'palla_conquistata', etichetta: 'Recuperata' },
    { tipo: 'palla_persa', etichetta: 'Persa' },
  ],
  [
    { tipo: 'tiro_specchio', etichetta: 'In porta' },
    { tipo: 'tiro_fuori', etichetta: 'Fuori' },
  ],
];

/**
 * Eventi a gioco fermo: rari, e registrati quando c'e il tempo di scegliere
 * il giocatore. Stanno nella fascia in basso e aprono la scelta.
 */
const A_GIOCO_FERMO: { tipo: TipoEvento; etichetta: string }[] = [
  { tipo: 'gol', etichetta: 'Gol' },
  { tipo: 'autogol', etichetta: 'Autogol' },
  { tipo: 'cartellino_giallo', etichetta: 'Giallo' },
  { tipo: 'cartellino_rosso', etichetta: 'Rosso' },
];

/** Un cartellino tocca anche chi e in panchina; gol e autogol solo chi gioca. */
const ANCHE_PANCHINA: TipoEvento[] = ['cartellino_giallo', 'cartellino_rosso'];

const STATO_CRONOMETRO: Record<
  StatoCronometro,
  { testo: string; variante: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  in_gioco: { testo: 'In gioco', variante: 'default' },
  tra_periodi: { testo: 'Intervallo', variante: 'secondary' },
  sospesa: { testo: 'Sospesa', variante: 'destructive' },
  terminata: { testo: 'Terminata', variante: 'outline' },
};

/** Un gol col marcatore scelto, in attesa di sapere chi ha fatto l'assist. */
interface GolInAttesa {
  marcatoreId: string;
  quando: Momento;
  /** Il minuto da mostrare, com'era quando il gol e stato segnato. */
  minuto: string;
}

/** Il cambio aperto: parte da chi esce o da chi entra. */
interface Cambio {
  verso: 'esce' | 'entra';
  giocatoreId: string;
}

/** Le conferme della schermata: una sola aperta alla volta. */
interface Conferma {
  titolo: string;
  testo: string;
  azione: string;
  esegui: () => void;
}

export function SchermataTimer({
  partitaId,
  rosa,
  tipiAttivi,
  onEsci,
  onFuoriDalCampo,
}: {
  partitaId: string;
  rosa: Giocatore[];
  tipiAttivi: TipoEvento[];
  onEsci: () => void;
  /**
   * La partita ha lasciato gli stati del timer: conclusa col fischio finale
   * o riportata a programmata svuotando la formazione. La pagina cambia forma.
   */
  onFuoriDalCampo: () => void;
}) {
  const t = useTimerPartita(partitaId);
  const [apertoId, setApertoId] = useState<string | null>(null);
  const [avviso, setAvviso] = useState<string | null>(null);
  const [elencoAperto, setElencoAperto] = useState(false);
  const [presaForzata, setPresaForzata] = useState<{ inattivaDaMs: number } | null>(null);
  const [conferma, setConferma] = useState<Conferma | null>(null);
  const [scelta, setScelta] = useState<TipoEvento | null>(null);
  const [golInAttesa, setGolInAttesa] = useState<GolInAttesa | null>(null);
  const [cambio, setCambio] = useState<Cambio | null>(null);

  const richiediControllo = async (forza: boolean) => {
    const esito = await t.azioni.prendiControllo(forza);
    setPresaForzata(esito.preso ? null : { inattivaDaMs: esito.inattivaDaMs });
  };

  useSchermoAcceso(t.stato?.partita.stato === 'in_corso');

  const statoPartita = t.stato?.partita.stato;
  useEffect(() => {
    if (statoPartita === 'conclusa' || statoPartita === 'programmata') onFuoriDalCampo();
  }, [statoPartita, onFuoriDalCampo]);

  if (!t.stato || !t.cronometro || !t.campo || !t.tabellino) {
    return <p className="attesa">Carico la partita…</p>;
  }

  // Un gemello spento lascia la sua casella vuota: le coppie restano in colonna.
  const gemelli = GEMELLI.map((coppia) =>
    coppia.map((a) => (tipiAttivi.includes(a.tipo) ? a : null)),
  ).filter((coppia) => coppia.some(Boolean));
  // Autogol resta anche se spento: il suo dialog porta l'autogol avversario,
  // che non si configura.
  const aGiocoFermo = A_GIOCO_FERMO.filter(
    (a) => tipiAttivi.includes(a.tipo) || a.tipo === 'autogol',
  );

  /**
   * Prima del fischio d'inizio gli eventi di ingresso non esistono ancora:
   * li genera "Inizia la partita". In campo si mostrano allora i titolari
   * della formazione confermata, e nessun comando sui giocatori: la
   * formazione si cambia svuotandola, non a colpi di "Entra".
   */
  const primaDelFischio = t.stato.partita.stato === 'formazione';
  const dentro = (g: Giocatore) => (primaDelFischio ? g.titolare : t.campo!.inCampo.has(g.id));
  // Solo i convocati: chi non lo e non puo entrare. Resta visibile chi
  // compare comunque negli eventi, per non far sparire una riga del referto.
  const disponibili = perCognome(
    rosa.filter((g) => g.convocato || t.campo!.inCampo.has(g.id) || t.campo!.espulsi.has(g.id)),
  );
  const inCampo = disponibili.filter(dentro);
  const inPanchina = disponibili.filter((g) => !dentro(g) && !t.campo!.espulsi.has(g.id));
  const espulsi = disponibili.filter((g) => t.campo!.espulsi.has(g.id));
  // Nessun vincolo sui cambi: un portiere in meno o in piu si segnala e basta.
  const avvisoCampo =
    inCampo.length > 0
      ? avvisoPortieri(
          inCampo.map((g) => g.ruolo),
          'in campo',
        )
      : null;
  const comandaGiocatori = t.modo === 'comando' && !primaDelFischio;
  const risultato = punteggio(t.tabellino.risultato, t.stato.partita.inCasa);
  /**
   * Minuti giocati finora, gli stessi del tabellino: nelle giovanili il
   * minutaggio si distribuisce, e chi e fermo a zero deve saltare all'occhio.
   * Prima del fischio non c'e niente da contare.
   */
  const minutiDi = new Map(t.tabellino.statistiche.map((s) => [s.giocatoreId, s.minuti]));
  const minuti = (id: string) => (primaDelFischio ? undefined : (minutiDi.get(id) ?? 0));
  const statisticheDi = new Map(t.tabellino.statistiche.map((s) => [s.giocatoreId, s]));
  const statoCronometro = primaDelFischio
    ? ({ testo: 'Da iniziare', variante: 'outline' } as const)
    : STATO_CRONOMETRO[t.cronometro.stato];

  /**
   * Registra e conferma con un toast: in piedi a bordo campo il dito manca
   * il pulsante, e l'occhio torna sul gioco. «Annulla» toglie l'evento, e il
   * rosso che il secondo giallo porta con se. Un id fisso fa si che il nuovo
   * toast sostituisca il precedente invece di impilarsi a ogni tocco.
   */
  const annuncia = (eventi: EventoIn[], testo: string, minuto: string) => {
    if (eventi.length === 0) return;
    toast.success(testo, {
      id: 'evento-live',
      description: minuto,
      duration: 5000,
      action: {
        label: 'Annulla',
        onClick: (click) => {
          // Senza, Sonner chiude il toast dopo il clic e con lui l'esito
          // qui sotto, che ne riusa l'id.
          click.preventDefault();
          t.azioni.annulla(eventi.map((e) => e.id));
          toast.info(`Annullato: ${testo}`, {
            id: 'evento-live',
            duration: 2500,
            // Lo stesso id aggiorna il toast sul posto: minuto e «Annulla»
            // di prima resterebbero.
            description: undefined,
            action: undefined,
          });
        },
      },
    });
  };

  const nomeDi = (id: string) => nomeBreve(rosa.find((g) => g.id === id));

  const registra = (tipo: TipoEvento, giocatoreId: string | null) => {
    const eventi = t.azioni.registra(tipo, giocatoreId);
    const titolo = eventi.some((e) => e.tipo === 'cartellino_rosso')
      ? 'Secondo giallo: espulso'
      : ETICHETTA_EVENTO[tipo];
    annuncia(
      eventi,
      giocatoreId ? `${titolo} · ${nomeDi(giocatoreId)}` : titolo,
      t.cronometro!.etichetta,
    );
  };

  /**
   * Il gol aspetta la scelta dell'assist, al minuto in cui e stato segnato.
   * Si registrano in ordine, prima l'assist e poi il gol: l'ora vera del gol
   * e un millisecondo dopo, perche a parita di minuto e secondo e lei a
   * mettere in fila gli eventi. Un solo «Annulla» toglie tutti e due.
   */
  const segnaGol = (gol: GolInAttesa, assistId: string | null) => {
    const dopo = new Date(Date.parse(gol.quando.orarioReale) + 1).toISOString();
    const eventi = [
      ...(assistId ? t.azioni.registra('assist', assistId, gol.quando) : []),
      ...t.azioni.registra('gol', gol.marcatoreId, { ...gol.quando, orarioReale: dopo }),
    ];
    const testo = `Gol · ${nomeDi(gol.marcatoreId)}`;
    annuncia(eventi, assistId ? `${testo}, assist ${nomeDi(assistId)}` : testo, gol.minuto);
    setGolInAttesa(null);
  };

  const tocca = (tipo: TipoEvento, giocatoreId: string) => {
    registra(tipo, giocatoreId);
    setApertoId(null);
  };

  /**
   * Esegue il cambio scelto nel dialog. La riga scivola nell'altra lista
   * invece di sparire da sotto il dito: chi guarda la partita deve ritrovare
   * con l'occhio dove e finita.
   */
  const cambia = (esceId: string | null, entraId: string | null) => {
    setCambio(null);
    const applica = () => {
      const esito = t.azioni.cambia(esceId, entraId);
      // Un cambio riuscito toglie l'avviso di prima, che non descrive piu il campo.
      setAvviso(esito.ok ? null : (esito.messaggio ?? null));
      const testo =
        esceId && entraId
          ? `Cambio · esce ${nomeDi(esceId)}, entra ${nomeDi(entraId)}`
          : esceId
            ? `Esce ${nomeDi(esceId)}`
            : `Entra ${nomeDi(entraId!)}`;
      annuncia(esito.eventi, testo, t.cronometro!.etichetta);
    };
    if (!document.startViewTransition || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      applica();
      return;
    }
    document.startViewTransition(() => flushSync(applica));
  };

  const comandoPeriodo = () => {
    const c = t.cronometro!;
    if (c.prossimaAzione === 'inizia_periodo') {
      return t.stato!.partita.stato === 'formazione'
        ? { testo: 'Inizia la partita', icona: Play, fn: () => esegui(t.azioni.avvia()) }
        : {
            testo: `Inizia il ${c.periodo}° periodo`,
            icona: Play,
            fn: () => riparti('periodo', c.periodo),
          };
    }
    if (c.prossimaAzione === 'termina_periodo') {
      const ultimo = c.periodo >= t.stato!.formato.nPeriodi;
      const testo = ultimo ? 'Fine partita' : `Fine ${c.periodo}° periodo`;
      // Un fischio toccato per sbaglio ferma il cronometro di tutti: si
      // chiede conferma, al costo di un tocco due o tre volte a partita.
      return {
        testo,
        icona: Whistle,
        fn: () =>
          setConferma({
            titolo: `${testo}?`,
            testo: ultimo
              ? 'Il cronometro si ferma e la partita si chiude: si passa al tabellino.'
              : `Il cronometro si ferma fino all'inizio del ${c.periodo + 1}° periodo.`,
            azione: testo,
            esegui: () => esegui(t.azioni.comandoPeriodo('termina')),
          }),
      };
    }
    if (c.prossimaAzione === 'riprendi') {
      return {
        testo: 'Riprendi il gioco',
        icona: Play,
        fn: () => riparti('ripresa', c.periodo),
      };
    }
    return null;
  };

  /** Un comando rifiutato dal server si spiega nella testata, non si perde. */
  const esegui = (comando: Promise<unknown>) =>
    void comando.then(
      () => setAvviso(null),
      (e: unknown) => setAvviso(e instanceof Error ? e.message : 'Comando non riuscito.'),
    );

  /**
   * Chi e uscito senza un sostituto lascia la squadra in meno: il gioco non
   * riparte, a inizio periodo come dopo una sospensione, finche si puo
   * rimediare dalla panchina, e in inferiorita vera solo dopo una conferma.
   * Il server ripete la verifica.
   */
  const riparti = (momento: 'periodo' | 'ripresa', periodo: number) => {
    const esito = verificaRipartenza(
      t.stato!.formato,
      t.campo!,
      inPanchina.map((g) => g.id),
      momento === 'periodo' ? 'iniziare il periodo' : 'riprendere il gioco',
    );
    const vai = (inferiorita: boolean) =>
      esegui(
        momento === 'periodo'
          ? t.azioni.comandoPeriodo('inizia', inferiorita)
          : t.azioni.sospensione('chiudi', inferiorita),
      );
    if (esito.ok) return vai(false);
    if (esito.rimediabile) return setAvviso(esito.messaggio);
    const presenti = t.campo!.inCampo.size;
    setConferma({
      titolo:
        momento === 'periodo'
          ? `Iniziare il ${periodo}° periodo in ${presenti}?`
          : `Riprendere il gioco in ${presenti}?`,
      testo: esito.messaggio,
      azione: momento === 'periodo' ? 'Inizia' : 'Riprendi',
      esegui: () => vai(true),
    });
  };

  const comando = comandoPeriodo();

  /** Prima del fischio d'inizio la formazione si riapre: si torna ai convocati. */
  const svuotaFormazione = async () => {
    try {
      await chiama(`/partite/${partitaId}/formazione/svuota`, { metodo: 'POST' });
      onFuoriDalCampo();
    } catch (e) {
      setAvviso(e instanceof Error ? e.message : 'Operazione non riuscita.');
    }
  };

  /**
   * Il timer non ha menu (§1.3): si esce da qui, e a partita in corso solo
   * dopo una conferma. Il cronometro continua sul server, ma gli eventi
   * ancora in coda vivono in questa pagina e uscendo andrebbero persi.
   */
  const esci = () => {
    const inCorso = t.stato!.partita.stato === 'in_corso';
    if (!inCorso && t.inCoda === 0) {
      onEsci();
      return;
    }
    const perdita = t.inCoda > 0 ? ` ${t.inCoda} eventi non ancora salvati andranno persi.` : '';
    setConferma({
      titolo: 'Uscire dal timer?',
      testo: inCorso
        ? `La partita è in corso: il cronometro continua, ma da qui smetti di registrare.${perdita}`
        : perdita.trim(),
      azione: 'Esci',
      esegui: onEsci,
    });
  };

  const ComandoIcona = comando?.icona;

  return (
    <div className="timer flex min-h-dvh flex-col bg-background text-foreground">
      <header className="testata-timer sticky top-0 z-10 border-b-2 border-foreground bg-background px-4 pt-2.5 pb-2">
        <div className="flex items-baseline justify-between gap-3">
          <div className="cronometro" aria-live="off">
            {t.cronometro.etichetta}
          </div>
          <div className="text-3xl font-semibold tabular-nums">{risultato}</div>
        </div>
        <div className="mt-1 flex items-center justify-between gap-3 text-sm text-muted-foreground">
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span>
              {t.cronometro.periodo}° periodo · {t.stato.partita.avversario}
            </span>
            <Badge variant={statoCronometro.variante}>{statoCronometro.testo}</Badge>
          </span>
          {/* L'uscita sta in alto, lontano dal pollice: un tocco accidentale
              durante un'azione non deve portare fuori dalla panchina. */}
          <Button variant="ghost" onClick={esci} className="-mr-2 h-10">
            <ChevronLeft />
            Partite
          </Button>
        </div>
        {/* L'avviso risponde a un tocco (un cambio o un periodo rifiutati):
            sta nella testata fissa perche si veda anche a lista scorsa. */}
        {avviso && (
          <Alert className="mt-2 has-data-[slot=alert-action]:pr-28">
            <TriangleAlert />
            <AlertDescription>{avviso}</AlertDescription>
            <AlertAction>
              <Button variant="outline" onClick={() => setAvviso(null)}>
                Ho capito
              </Button>
            </AlertAction>
          </Alert>
        )}
      </header>

      {/* Gli avvisi stanno sotto il cronometro e non bloccano mai la
          registrazione: interrompere una partita per un banner sarebbe peggio
          del problema che segnala. */}
      <div className="flex flex-col gap-2 p-2 empty:hidden">
        {t.modo === 'perso' && (
          <Alert variant="destructive" className="has-data-[slot=alert-action]:pr-36">
            <OctagonX />
            <AlertDescription>Il controllo è passato a un altro dispositivo.</AlertDescription>
            <AlertAction>
              <Button variant="destructive" onClick={() => void t.azioni.prendiControllo(true)}>
                Riprendi tu
              </Button>
            </AlertAction>
          </Alert>
        )}
        {primaDelFischio && (
          <Alert role="status">
            <Info />
            <AlertDescription>
              Formazione confermata. Al fischio d&apos;inizio premi «Inizia la partita»: i titolari
              scendono in campo e il cronometro parte. Per cambiarla, svuotala.
            </AlertDescription>
          </Alert>
        )}
        {t.modo === 'lettura' && !(primaDelFischio && !t.stato.sessione) && (
          <Alert className="has-data-[slot=alert-action]:pr-44">
            <Info />
            <AlertDescription>
              {presaForzata
                ? `In uso da ${Math.round(presaForzata.inattivaDaMs / 1000)}s. Vuoi prendere comunque il controllo?`
                : 'Sola lettura: nessun controllo su questa partita.'}
            </AlertDescription>
            <AlertAction>
              <Button
                variant="outline"
                onClick={() => void richiediControllo(presaForzata !== null)}
              >
                {presaForzata ? 'Conferma' : 'Richiedi il controllo'}
              </Button>
            </AlertAction>
          </Alert>
        )}
        {t.inCoda > 0 && (
          <Alert role="status" variant={t.allarmeInvio ? 'destructive' : 'default'}>
            {t.allarmeInvio ? <OctagonX /> : <Info />}
            <AlertDescription>
              {t.allarmeInvio
                ? `${t.inCoda} eventi non salvati da oltre due minuti. Annotali a mano: potrai inserirli a fine partita.`
                : `${t.inCoda} eventi in attesa di salvataggio.`}
            </AlertDescription>
          </Alert>
        )}
      </div>

      <main className="flex-1 overflow-y-auto">
        <TitoloGruppo
          testo="In campo"
          numero={inCampo.length}
          transizione="titolo-campo"
          avviso={avvisoCampo}
        />
        {/* In campo si cerca per reparto: il ruolo sta nel sottotitolo e
            non si ripete sotto il nome. */}
        {RUOLI_GIOCATORE.map((ruolo) => {
          const reparto = inCampo.filter((g) => g.ruolo === ruolo);
          if (reparto.length === 0) return null;
          return (
            <section key={ruolo}>
              <h3 className="bg-muted px-4 py-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {reparto.length === 1 ? etichettaRuolo(ruolo) : REPARTI[repartoDi(ruolo)]}
              </h3>
              {reparto.map((g) => (
                <RigaGiocatore
                  key={g.id}
                  giocatore={g}
                  dentro
                  senzaRuolo
                  aperto={apertoId === g.id}
                  gemelli={gemelli}
                  minuti={minuti(g.id)}
                  statistiche={statisticheDi.get(g.id)}
                  attivo={comandaGiocatori}
                  commutabile={!primaDelFischio}
                  onApri={() => setApertoId(apertoId === g.id ? null : g.id)}
                  onAzione={(tipo) => tocca(tipo, g.id)}
                  onCommuta={() => setCambio({ verso: 'esce', giocatoreId: g.id })}
                />
              ))}
            </section>
          );
        })}

        <TitoloGruppo
          testo="In panchina"
          numero={inPanchina.length}
          transizione="titolo-panchina"
        />
        {inPanchina.map((g) => (
          <RigaGiocatore
            key={g.id}
            giocatore={g}
            dentro={false}
            aperto={false}
            gemelli={[]}
            minuti={minuti(g.id)}
            statistiche={statisticheDi.get(g.id)}
            attivo={comandaGiocatori}
            commutabile={!primaDelFischio}
            onApri={() => undefined}
            onAzione={() => undefined}
            onCommuta={() => setCambio({ verso: 'entra', giocatoreId: g.id })}
          />
        ))}

        {espulsi.length > 0 && (
          <>
            <TitoloGruppo testo="Espulsi" numero={espulsi.length} />
            {espulsi.map((g) => (
              <div
                key={g.id}
                className="flex min-h-14 items-center gap-3 border-b px-4 text-muted-foreground"
              >
                <Numero numero={g.numero} />
                <span className="min-w-0 flex-1 truncate text-lg font-semibold line-through">
                  {g.cognome} {g.nome.charAt(0)}.
                </span>
                {/* Il rosso c'e sempre, anche quando l'espulsione viene dal doppio giallo. */}
                <Segni
                  statistiche={{
                    gol: statisticheDi.get(g.id)?.gol ?? 0,
                    gialli: statisticheDi.get(g.id)?.gialli ?? 0,
                    rossi: 1,
                  }}
                />
                <Minuti minuti={minuti(g.id)} />
              </div>
            ))}
          </>
        )}
      </main>

      {/* In fondo perche e la zona che il pollice raggiunge senza cambiare presa. */}
      <footer className="fascia-timer sticky bottom-0 flex flex-col gap-2 border-t-2 border-foreground bg-background px-4 py-2.5">
        <div className="flex gap-2">
          {aGiocoFermo.map((a) => (
            <PulsanteEvento
              key={a.tipo}
              tipo={a.tipo}
              className="azione-evento h-14 min-w-0 flex-1 flex-col gap-0.5 px-1 text-xs leading-tight font-semibold whitespace-normal"
              disabled={!comandaGiocatori}
              onClick={() => setScelta(a.tipo)}
            >
              {a.etichetta}
            </PulsanteEvento>
          ))}
          <PulsanteEvento
            tipo="gol_subito"
            className="azione-evento h-14 min-w-0 flex-1 flex-col gap-0.5 px-1 text-xs leading-tight font-semibold whitespace-normal"
            disabled={t.modo !== 'comando'}
            onClick={() => registra('gol_subito', null)}
          >
            Gol subito
          </PulsanteEvento>
        </div>
        {/* Tre pulsanti su un telefono stretto: nessuno puo allargare la
            riga. Eventi e compatto come la riga sopra, il comando del periodo
            va a capo invece di uscire dallo schermo. */}
        <div className="flex gap-2">
          {primaDelFischio ? (
            <Button
              variant="outline"
              className="h-14 min-w-0 flex-1 text-base leading-tight font-semibold whitespace-normal"
              onClick={() =>
                setConferma({
                  titolo: 'Svuotare la formazione?',
                  testo: 'I convocati restano, i titolari si riscelgono.',
                  azione: 'Svuota',
                  esegui: () => void svuotaFormazione(),
                })
              }
            >
              Svuota formazione
            </Button>
          ) : (
            <Button
              variant="outline"
              className="h-14 min-w-0 flex-1 flex-col gap-0.5 px-1 text-xs font-semibold"
              onClick={() => setElencoAperto(true)}
            >
              {/* Il numero accanto all'icona: con i passaggi una partita supera
                  il centinaio di eventi, e "Eventi · 150" non sta a 320px. */}
              <span className="flex items-center gap-1 text-sm tabular-nums">
                <ListOrdered className="size-5" />
                {t.tabellino.voci.length}
              </span>
              Eventi
            </Button>
          )}
          {t.cronometro.stato === 'in_gioco' && (
            // Solo icona: la pausa si riconosce, e la riga ha gia due pulsanti larghi.
            <Button
              variant="outline"
              size="icon-lg"
              className="size-14"
              aria-label="Sospendi la partita"
              disabled={t.modo !== 'comando'}
              onClick={() =>
                // La sospensione ferma il cronometro e toglie minuti a tutti
                // quelli in campo: la conferma ricorda a cosa serve.
                setConferma({
                  titolo: 'Sospendere la partita?',
                  testo:
                    'Il cronometro si ferma finché non riprendi il gioco. È per le interruzioni vere (maltempo, un infortunio grave, il campo da liberare), non per i soliti infortuni: usata male falsa i minuti di tutti.',
                  azione: 'Sospendi',
                  esegui: () => esegui(t.azioni.sospensione('apri')),
                })
              }
            >
              <Pause className="size-6" />
            </Button>
          )}
          {comando && ComandoIcona && (
            <Button
              className="h-14 min-w-0 flex-[2] px-2 text-base leading-tight font-semibold whitespace-normal"
              onClick={comando.fn}
            >
              <ComandoIcona className="size-5" />
              {comando.testo}
            </Button>
          )}
        </div>
      </footer>

      <ElencoEventi
        aperto={elencoAperto}
        voci={t.tabellino.voci}
        rosa={rosa}
        onCancella={(id) => void t.azioni.cancella(id)}
        onChiudi={() => setElencoAperto(false)}
      />

      <SceltaGiocatore
        tipo={scelta}
        minuto={t.cronometro.etichetta}
        inCampo={inCampo}
        inPanchina={inPanchina}
        autogolNostro={tipiAttivi.includes('autogol')}
        onScelto={(tipo, giocatoreId) => {
          setScelta(null);
          const quando = t.azioni.momento();
          if (tipo === 'gol' && giocatoreId && quando && tipiAttivi.includes('assist')) {
            setGolInAttesa({ marcatoreId: giocatoreId, quando, minuto: t.cronometro!.etichetta });
            return;
          }
          registra(tipo, giocatoreId);
        }}
        onChiudi={() => setScelta(null)}
      />

      <DialogCambio
        cambio={cambio}
        formato={t.stato.formato}
        campo={t.campo}
        inCampo={inCampo}
        inPanchina={inPanchina}
        minuto={t.cronometro.etichetta}
        nomeDi={nomeDi}
        onCambia={cambia}
        onChiudi={() => setCambio(null)}
      />

      <SceltaAssist
        gol={golInAttesa}
        marcatore={golInAttesa ? nomeDi(golInAttesa.marcatoreId) : ''}
        inCampo={inCampo.filter((g) => g.id !== golInAttesa?.marcatoreId)}
        onScelto={(assistId) => golInAttesa && segnaGol(golInAttesa, assistId)}
      />

      <AlertDialog open={conferma !== null} onOpenChange={(o) => !o && setConferma(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{conferma?.titolo}</AlertDialogTitle>
            {conferma?.testo && <AlertDialogDescription>{conferma.testo}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Annulla</AlertDialogCancel>
            <AlertDialogAction className="h-12" onClick={() => conferma?.esegui()}>
              {conferma?.azione}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TitoloGruppo({
  testo,
  numero,
  transizione,
  avviso,
}: {
  testo: string;
  numero: number;
  /** Nome per la transizione del cambio, se il titolo si sposta con le righe. */
  transizione?: string;
  /**
   * Accanto al titolo e non in un banner: un avviso che dura mezza partita,
   * come il portiere espulso, non deve spingere giu le righe.
   */
  avviso?: string | null;
}) {
  return (
    <h2
      className="flex items-center gap-2 px-4 pt-3.5 pb-1.5 text-sm font-semibold text-muted-foreground"
      style={transizione ? { viewTransitionName: transizione } : undefined}
    >
      {testo}
      <Badge variant="secondary" className="tabular-nums">
        {numero}
      </Badge>
      {avviso && (
        <Badge role="status" className="bg-avviso text-avviso-foreground">
          <TriangleAlert />
          {avviso}
        </Badge>
      )}
    </h2>
  );
}

function Numero({ numero, className }: { numero: number | null; className?: string }) {
  return (
    <span className={cn('min-w-7 text-sm font-bold text-muted-foreground tabular-nums', className)}>
      {numero ?? '—'}
    </span>
  );
}

/** Minuti giocati in fondo alla riga; zero resta visibile, e il dato che serve. */
/**
 * Gol e cartellini accanto al nome: chi e ammonito si vede prima di
 * rischiare il secondo giallo. Il rosso non ha numero, ce n'e uno solo.
 */
function Segni({
  statistiche,
}: {
  statistiche: Pick<StatisticheGiocatore, 'gol' | 'gialli' | 'rossi'> | undefined;
}) {
  if (!statistiche) return null;
  const { gol, gialli, rossi } = statistiche;
  if (gol + gialli + rossi === 0) return null;
  return (
    <span className="flex shrink-0 items-center gap-2 text-sm font-bold tabular-nums">
      {gol > 0 && (
        <span className="flex items-center gap-0.5" aria-label={`${gol} gol`}>
          {gol}
          <IconaEvento tipo="gol" />
        </span>
      )}
      {gialli > 0 && (
        <span className="flex items-center gap-0.5" aria-label={`${gialli} gialli`}>
          {gialli}
          <IconaEvento tipo="cartellino_giallo" />
        </span>
      )}
      {rossi > 0 && (
        <span aria-label="Espulso">
          <IconaEvento tipo="cartellino_rosso" />
        </span>
      )}
    </span>
  );
}

function Minuti({ minuti, className }: { minuti: number | undefined; className?: string }) {
  if (minuti === undefined) return null;
  return (
    <span
      className={cn('ml-auto shrink-0 text-base text-muted-foreground tabular-nums', className)}
      aria-label={`${minuti} minuti giocati`}
    >
      {minuti}′
    </span>
  );
}

function RigaGiocatore({
  giocatore,
  dentro,
  aperto: apertoRichiesto,
  gemelli,
  minuti,
  statistiche,
  attivo,
  commutabile,
  onApri,
  onAzione,
  onCommuta,
  senzaRuolo = false,
}: {
  giocatore: Pick<Giocatore, 'id' | 'numero' | 'cognome' | 'nome' | 'ruolo'>;
  dentro: boolean;
  /** Il ruolo lo dice gia il reparto sopra la riga. */
  senzaRuolo?: boolean;
  aperto: boolean;
  /** Coppie di eventi; null e la casella di un gemello spento. */
  gemelli: ({ tipo: TipoEvento; etichetta: string } | null)[][];
  /** Assente prima del fischio. */
  minuti: number | undefined;
  /** Gol e cartellini della partita, assenti finche non ce ne sono. */
  statistiche: Pick<StatisticheGiocatore, 'gol' | 'gialli' | 'rossi'> | undefined;
  attivo: boolean;
  /** Falso prima del fischio: la riga mostra solo chi e titolare. */
  commutabile: boolean;
  onApri: () => void;
  onAzione: (tipo: TipoEvento) => void;
  onCommuta: () => void;
}) {
  // Da tablet in su gli eventi stanno sulla riga, senza cassetto: c'e posto,
  // e un tocco in meno a ogni evento.
  const largo = useLargo();
  const inRiga = largo && dentro && gemelli.length > 0;
  const aperto = apertoRichiesto && !inRiga;
  const nome = (
    <>
      <Numero numero={giocatore.numero} className={cn(aperto && 'text-current')} />
      <span className="flex min-w-0 flex-1 flex-col items-start leading-tight">
        <span className="max-w-full truncate">
          {giocatore.cognome} {giocatore.nome.charAt(0)}.
        </span>
        {!senzaRuolo && (
          <span
            className={cn(
              'max-w-full truncate text-xs font-normal text-muted-foreground',
              aperto && 'text-current',
            )}
          >
            {etichettaRuolo(giocatore.ruolo)}
          </span>
        )}
      </span>
      <Segni statistiche={statistiche} />
      <Minuti minuti={minuti} className={cn(aperto && 'text-current')} />
    </>
  );
  return (
    <div
      className={cn('border-b', aperto && 'bg-secondary text-secondary-foreground')}
      // Il nome lega la riga a se stessa fra una lista e l'altra nella transizione del cambio.
      style={{ viewTransitionName: `riga-${giocatore.id}` }}
    >
      <div className="flex items-stretch">
        {inRiga ? (
          <>
            <div className="flex h-14 min-w-0 flex-1 items-center gap-3 px-4 text-lg font-semibold">
              {nome}
            </div>
            <div className="flex shrink-0 items-center gap-1.5 px-2">
              {gemelli.flat().map((a, i) =>
                a ? (
                  <PulsanteEvento
                    key={a.tipo}
                    tipo={a.tipo}
                    className="azione-evento h-11 w-18 flex-col gap-0.5 px-1 text-xs leading-tight font-semibold lg:w-24"
                    disabled={!attivo}
                    onClick={() => onAzione(a.tipo)}
                  >
                    {a.etichetta}
                  </PulsanteEvento>
                ) : (
                  <div key={`vuoto-${i}`} aria-hidden className="w-18 lg:w-24" />
                ),
              )}
            </div>
          </>
        ) : (
          /* Una riga spenta resta leggibile: la formazione si guarda anche
             prima del fischio e in sola lettura. */
          <Button
            variant="ghost"
            className="h-14 min-w-0 flex-1 justify-start gap-3 rounded-none px-4 text-lg font-semibold disabled:text-muted-foreground disabled:opacity-100 aria-expanded:bg-transparent aria-expanded:text-secondary-foreground"
            onClick={onApri}
            disabled={!dentro || !attivo}
            aria-expanded={aperto}
          >
            {nome}
          </Button>
        )}

        {commutabile && (
          <Button
            variant="ghost"
            className={cn(
              'h-14 w-16 rounded-none border-l',
              // Sul fondo del cassetto aperto il rosso non si legge.
              aperto ? 'text-current' : dentro ? 'text-evento-cattivo' : 'text-evento-neutro',
            )}
            onClick={onCommuta}
            disabled={!attivo}
            aria-label={dentro ? 'Manda in panchina' : 'Manda in campo'}
          >
            {dentro ? <LogOut className="size-6" /> : <LogIn className="size-6" />}
          </Button>
        )}
      </div>

      {aperto && (
        <div className="grid grid-cols-2 gap-2 px-4 pt-1 pb-3.5 sm:grid-cols-4 lg:grid-cols-6">
          {gemelli.flat().map((a, i) =>
            a ? (
              <PulsanteEvento
                key={a.tipo}
                tipo={a.tipo}
                className="azione-evento h-13 justify-start gap-2 px-3 text-base font-semibold"
                onClick={() => onAzione(a.tipo)}
              >
                {a.etichetta}
              </PulsanteEvento>
            ) : (
              <div key={`vuoto-${i}`} aria-hidden />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function ElencoEventi({
  aperto,
  voci,
  rosa,
  onCancella,
  onChiudi,
}: {
  aperto: boolean;
  voci: {
    eventoId: string;
    etichetta: string;
    tipo: TipoEvento | 'sostituzione';
    giocatoreId: string | null;
    giocatoreUscenteId?: string;
  }[];
  rosa: Giocatore[];
  onCancella: (id: string) => void;
  onChiudi: () => void;
}) {
  const per = new Map(rosa.map((g) => [g.id, g]));
  const nome = (id: string | null | undefined) => nomeBreve(id ? per.get(id) : undefined);

  const descrizione = (v: (typeof voci)[number]): string => {
    // Una sostituzione tiene sempre chi esce (evento indipendente e
    // cancellabile a se) e chi entra: senza entrambi il referto mente su meta
    // del cambio.
    if (v.tipo === 'sostituzione') {
      return v.giocatoreUscenteId
        ? `Esce ${nome(v.giocatoreUscenteId)}, entra ${nome(v.giocatoreId)}`
        : `Entra ${nome(v.giocatoreId)}`;
    }
    const etichetta = ETICHETTA_EVENTO[v.tipo];
    return v.giocatoreId ? `${etichetta} · ${nome(v.giocatoreId)}` : etichetta;
  };

  return (
    <Sheet open={aperto} onOpenChange={(o) => !o && onChiudi()}>
      <SheetContent side="bottom" className="timer gap-0 data-[side=bottom]:h-dvh">
        <SheetHeader className="border-b-2 border-foreground">
          <SheetTitle className="text-xl font-bold">Eventi</SheetTitle>
          <SheetDescription>Dal più recente. Gli eventi di periodo non compaiono.</SheetDescription>
        </SheetHeader>
        <ol className="flex-1 overflow-y-auto">
          {voci.length === 0 && (
            <li className="px-4 py-6 text-muted-foreground">Nessun evento registrato.</li>
          )}
          {[...voci].reverse().map((v) => (
            <li key={v.eventoId} className="flex items-center gap-3 border-b px-4 py-2">
              <span className="w-14 shrink-0 font-bold tabular-nums">{v.etichetta}</span>
              <IconaEvento tipo={v.tipo} className="size-5" />
              <span className="flex-1 text-base">{descrizione(v)}</span>
              <Button
                variant="destructive"
                size="icon-lg"
                className="size-12"
                onClick={() => onCancella(v.eventoId)}
                aria-label={`Elimina: ${descrizione(v)}`}
              >
                <Trash2 className="size-5" />
              </Button>
            </li>
          ))}
        </ol>
        <SheetFooter className="border-t">
          <SheetClose asChild>
            <Button variant="outline" className="h-12 text-base font-semibold">
              Chiudi
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/** In panchina si cerca il nome, non la maglia: ordine per cognome e nome. */
function perCognome(giocatori: Giocatore[]): Giocatore[] {
  return [...giocatori].sort(
    (a, b) => a.cognome.localeCompare(b.cognome, 'it') || a.nome.localeCompare(b.nome, 'it'),
  );
}

/**
 * Scelta del giocatore per un evento a gioco fermo. Un tocco registra e
 * chiude; il minuto e quello del tocco, come per gli eventi di gioco.
 * L'autogol offre anche quello di un avversario, che non ha giocatore e
 * resta l'unica scelta se l'autogol dei nostri e spento.
 */
function SceltaGiocatore({
  tipo: tipoAperto,
  minuto,
  inCampo,
  inPanchina,
  autogolNostro,
  onScelto,
  onChiudi,
}: {
  tipo: TipoEvento | null;
  minuto: string;
  inCampo: Giocatore[];
  inPanchina: Giocatore[];
  autogolNostro: boolean;
  onScelto: (tipo: TipoEvento, giocatoreId: string | null) => void;
  onChiudi: () => void;
}) {
  // Chiuso, il dialog anima ancora un attimo: con l'ultimo tipo scelto, e non
  // con null, non cambia contenuto mentre sparisce.
  const [ultimo, setUltimo] = useState(tipoAperto);
  if (tipoAperto !== null && tipoAperto !== ultimo) setUltimo(tipoAperto);
  const tipo = tipoAperto ?? ultimo;
  const autogol = tipo === 'autogol';
  const gruppi =
    autogol && !autogolNostro
      ? []
      : [
          { titolo: 'In campo', giocatori: inCampo },
          ...(tipo && ANCHE_PANCHINA.includes(tipo)
            ? [{ titolo: 'In panchina', giocatori: inPanchina }]
            : []),
        ];

  return (
    <Dialog open={tipoAperto !== null} onOpenChange={(o) => !o && onChiudi()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            {tipo && <IconaEvento tipo={tipo} className="size-5" />}
            {tipo && ETICHETTA_EVENTO[tipo]} · {minuto}
          </DialogTitle>
          <DialogDescription>
            {!autogol
              ? 'Scegli il giocatore.'
              : autogolNostro
                ? 'Scegli il giocatore, o segna l’autogol di un avversario.'
                : 'Un autogol degli avversari conta per noi.'}
          </DialogDescription>
        </DialogHeader>
        {autogol && (
          <PulsanteEvento
            tipo="autogol_avversario"
            className="azione-evento h-14 gap-2 text-base font-semibold"
            onClick={() => onScelto('autogol_avversario', null)}
          >
            {ETICHETTA_EVENTO.autogol_avversario}
          </PulsanteEvento>
        )}
        {gruppi.map((g) => (
          <section key={g.titolo} className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-muted-foreground">{g.titolo}</h3>
            <div className="grid grid-cols-2 gap-2">
              {g.giocatori.map((giocatore) => (
                <Button
                  key={giocatore.id}
                  variant="outline"
                  className="h-14 justify-start gap-2 px-3 text-base font-semibold"
                  onClick={() => tipo && onScelto(tipo, giocatore.id)}
                >
                  <Numero numero={giocatore.numero} />
                  <span className="truncate">
                    {giocatore.cognome} {giocatore.nome.charAt(0)}.
                  </span>
                </Button>
              ))}
            </div>
          </section>
        ))}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="h-12 text-base">
              Annulla
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Dopo il marcatore, chi ha fatto l'assist. Facoltativo: «Nessun assist», o
 * chiudere il dialog, registra il gol da solo. Il gol non si perde mai per
 * strada: chiudere non lo annulla, per quello c'e il toast.
 */
function SceltaAssist({
  gol: golAperto,
  marcatore,
  inCampo,
  onScelto,
}: {
  gol: GolInAttesa | null;
  marcatore: string;
  inCampo: Giocatore[];
  onScelto: (assistId: string | null) => void;
}) {
  // Come in SceltaGiocatore: chiuso, il dialog anima ancora un attimo con
  // l'ultimo gol, e non cambia contenuto mentre sparisce.
  const [ultimo, setUltimo] = useState(golAperto);
  const [ultimoMarcatore, setUltimoMarcatore] = useState(marcatore);
  if (golAperto !== null && golAperto !== ultimo) {
    setUltimo(golAperto);
    setUltimoMarcatore(marcatore);
  }
  const gol = golAperto ?? ultimo;

  return (
    <Dialog open={golAperto !== null} onOpenChange={(o) => !o && onScelto(null)}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <IconaEvento tipo="assist" className="size-5" />
            Assist · {gol?.minuto}
          </DialogTitle>
          <DialogDescription>
            Gol di {golAperto ? marcatore : ultimoMarcatore}: scegli chi ha servito il passaggio, se
            c’è.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {inCampo.map((giocatore) => (
            <Button
              key={giocatore.id}
              variant="outline"
              className="h-14 justify-start gap-2 px-3 text-base font-semibold"
              onClick={() => onScelto(giocatore.id)}
            >
              <Numero numero={giocatore.numero} />
              <span className="truncate">
                {giocatore.cognome} {giocatore.nome.charAt(0)}.
              </span>
            </Button>
          ))}
        </div>
        <DialogFooter>
          <Button className="h-12 text-base" onClick={() => onScelto(null)}>
            Nessun assist
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Il cambio in un dialog: da chi esce si sceglie chi entra, da chi entra chi
 * esce. Chi entra si verifica come se l'altro fosse gia uscito: in panchina
 * compare solo chi puo davvero entrare (niente rientro vietato). Con i cambi
 * esauriti lo dice subito, e lascia solo uscire: un infortunato esce anche
 * senza cambio, e la squadra resta in uno in meno.
 */
function DialogCambio({
  cambio: cambioAperto,
  formato,
  campo,
  inCampo,
  inPanchina,
  minuto,
  nomeDi,
  onCambia,
  onChiudi,
}: {
  cambio: Cambio | null;
  formato: Formato;
  campo: StatoCampo;
  inCampo: Giocatore[];
  inPanchina: Giocatore[];
  minuto: string;
  nomeDi: (id: string) => string;
  onCambia: (esceId: string | null, entraId: string | null) => void;
  onChiudi: () => void;
}) {
  // Come negli altri dialog: chiuso, anima ancora un attimo con l'ultimo
  // cambio, e non cambia contenuto mentre sparisce.
  const [ultimo, setUltimo] = useState(cambioAperto);
  if (cambioAperto !== null && cambioAperto !== ultimo) setUltimo(cambioAperto);
  const cambio = cambioAperto ?? ultimo;
  if (!cambio) return null;

  const esce = cambio.verso === 'esce';
  const nome = nomeDi(cambio.giocatoreId);
  const esauriti = campo.sostituzioniUsate >= formato.maxSostituzioni;
  const senza = (id: string): StatoCampo => ({
    ...campo,
    inCampo: new Set([...campo.inCampo].filter((x) => x !== id)),
  });

  // Chi esce: in panchina chi puo entrare al suo posto. Chi entra: in campo
  // chiunque puo uscire, purche l'ingresso passi con lui fuori.
  const esitoIngresso = esce
    ? null
    : verificaIngresso(formato, senza(inCampo[0]?.id ?? ''), cambio.giocatoreId);
  const scelte = esce
    ? inPanchina.filter((g) => verificaIngresso(formato, senza(cambio.giocatoreId), g.id).ok)
    : esitoIngresso?.ok
      ? inCampo
      : [];
  // Con uno in meno in campo si entra anche senza far uscire nessuno.
  const entraDaSolo = !esce && verificaIngresso(formato, campo, cambio.giocatoreId).ok;

  const blocco = esauriti
    ? {
        titolo: 'Cambi esauriti',
        testo: esce
          ? `Usati ${campo.sostituzioniUsate} su ${formato.maxSostituzioni}: ${nome} può solo uscire, e la squadra resta in uno in meno.`
          : `Usati ${campo.sostituzioniUsate} su ${formato.maxSostituzioni}: ${nome} non può entrare.`,
      }
    : !esce && !esitoIngresso?.ok
      ? { titolo: 'Non può entrare', testo: esitoIngresso?.messaggio ?? '' }
      : esce && scelte.length === 0
        ? {
            titolo: 'Nessuno può entrare',
            testo: `In panchina non c’è chi possa prendere il posto di ${nome}: può solo uscire.`,
          }
        : null;

  return (
    <Dialog open={cambioAperto !== null} onOpenChange={(o) => !o && onChiudi()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <ArrowRightLeft className="size-5" />
            {esce ? `Esce ${nome}` : `Entra ${nome}`} · {minuto}
          </DialogTitle>
          <DialogDescription>
            {blocco
              ? 'Il cambio non si può fare.'
              : esce
                ? 'Scegli chi entra al suo posto.'
                : 'Scegli chi esce per farlo entrare.'}
          </DialogDescription>
        </DialogHeader>
        {blocco && (
          <Alert>
            <TriangleAlert />
            <AlertTitle>{blocco.titolo}</AlertTitle>
            <AlertDescription>{blocco.testo}</AlertDescription>
          </Alert>
        )}
        {!esauriti && scelte.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {scelte.map((g) => (
              <Button
                key={g.id}
                variant="outline"
                className="h-14 justify-start gap-2 px-3 text-base font-semibold"
                onClick={() =>
                  esce ? onCambia(cambio.giocatoreId, g.id) : onCambia(g.id, cambio.giocatoreId)
                }
              >
                <Numero numero={g.numero} />
                <span className="flex min-w-0 flex-col items-start leading-tight">
                  <span className="max-w-full truncate">
                    {g.cognome} {g.nome.charAt(0)}.
                  </span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {etichettaRuolo(g.ruolo)}
                  </span>
                </span>
              </Button>
            ))}
          </div>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="h-12 text-base">
              Annulla
            </Button>
          </DialogClose>
          {esce && (
            <Button
              variant={blocco ? 'default' : 'secondary'}
              className="h-12 text-base"
              onClick={() => onCambia(cambio.giocatoreId, null)}
            >
              Esce senza cambio
            </Button>
          )}
          {entraDaSolo && (
            <Button className="h-12 text-base" onClick={() => onCambia(null, cambio.giocatoreId)}>
              Entra senza cambio
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Finestra da tablet in su: la stessa soglia di `md` in Tailwind. */
const TABLET = '(min-width: 48rem)';

function useLargo(): boolean {
  return useSyncExternalStore(
    (avvisa) => {
      const mq = matchMedia(TABLET);
      mq.addEventListener('change', avvisa);
      return () => mq.removeEventListener('change', avvisa);
    },
    () => matchMedia(TABLET).matches,
  );
}
